import { useRef, useState } from "react";
import { api, formatApiErrorDetail, BACKEND } from "@/lib/api";
import { UploadCloud, Loader2 } from "lucide-react";

/**
 * Image upload input. Calls /api/upload/image then returns the public URL.
 * Usage: <ImageUploader value={url} onChange={(url) => setUrl(url)} />
 */
export default function ImageUploader({ value, onChange, label = "Image" }) {
  const fileRef = useRef();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const displayUrl = value?.startsWith("/api/") ? `${BACKEND}${value}` : value;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="image-uploader">
      <label className="overline block mb-3">{label}</label>
      <div className="flex items-start gap-4">
        <div className="w-32 h-32 border border-[#D9D3C5] bg-[#F6F0E7] flex items-center justify-center overflow-hidden shrink-0">
          {displayUrl
            ? <img src={displayUrl} alt="" className="w-full h-full object-cover" />
            : <span className="text-xs text-[#697585]">aucune</span>}
        </div>
        <div className="flex-1 space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleUpload}
            className="hidden"
            data-testid="upload-input"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={loading}
            className="btn-ghost"
            data-testid="upload-button"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
            {loading ? "Téléchargement…" : "Téléverser une image"}
          </button>
          <input
            type="text"
            placeholder="Ou collez une URL d'image"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            className="field"
            data-testid="upload-url-input"
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
          <p className="text-xs text-[#697585]">JPEG, PNG, WEBP, GIF — max 8 Mo</p>
        </div>
      </div>
    </div>
  );
}
